import React, { useState, useEffect } from 'react';
import {
  Autocomplete,
  TextField,
  CircularProgress,
  Box,
  Avatar,
  Typography,
} from '@mui/material';
import { chatService } from '../../services/chat.service';
import { ChatUser } from '../../types/chat.types';

interface UserSearchProps {
  onUserSelect: (user: ChatUser | null) => void;
}

export const UserSearch: React.FC<UserSearchProps> = ({ onUserSelect }) => {
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState<ChatUser[]>([]);
  const [loading, setLoading] = useState(false);
  const [inputValue, setInputValue] = useState('');

  useEffect(() => {
    let active = true;

    if (!open) {
      setOptions([]);
      return undefined;
    }

    const fetchData = async () => {
      setLoading(true);
      try {
        const users = await chatService.findChatUsers(inputValue);
        if (active) {
          setOptions(users);
        }
      } catch (error) {
        console.error('Помилка пошуку користувачів:', error);
        if (active) {
          setOptions([]);
        }
      }
      setLoading(false);
    };

    // Застосовуємо debounce, щоб не надсилати запит на кожне натискання
    const debounce = setTimeout(() => {
      fetchData();
    }, 300); // 300ms затримка

    return () => {
      clearTimeout(debounce);
      active = false;
    };
  }, [inputValue, open]);

  return (
    <Autocomplete
      id="user-search-autocomplete"
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      // Виправлення: прибираємо невикористовуваний параметр event
      onChange={(_, value) => onUserSelect(value)}
      onInputChange={(_, newInputValue) => {
        setInputValue(newInputValue);
      }}
      isOptionEqualToValue={(option, value) => option.id === value.id}
      getOptionLabel={(option) => option.name}
      options={options}
      loading={loading}
      filterOptions={(x) => x}
      renderInput={(params) => (
        <TextField
          {...params}
          label="Знайти співрозмовника..."
          margin="normal"
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress color="inherit" size={20} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
      renderOption={(props, option) => (
        <Box component="li" sx={{ '& > img': { mr: 2, flexShrink: 0 } }} {...props} key={option.id}>
          <Avatar src={option.avatar || undefined} sx={{ mr: 2 }} />
          <Typography variant="body1">{option.name}</Typography>
        </Box>
      )}
    />
  );
};